'use client'

import { MenuIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { BrandMark } from '@/components/brand-mark'
import { MarketingButton } from '@/components/marketing/marketing-button'
import { APP_HREF, MARKETING_NAV, SIGN_IN_HREF } from '@/lib/marketing/content'
import { cn } from '@/lib/utils'

const MOBILE_MENU_ID = 'marketing-mobile-menu'

export function SiteHeader({ isAuthenticated }: { isAuthenticated: boolean }) {
	const t = useTranslations('marketing.nav')
	const [open, setOpen] = useState(false)

	useEffect(() => {
		if (!open) return
		const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [open])

	const actions = isAuthenticated ? (
		<MarketingButton href={APP_HREF} size="sm" withMark>
			{t('openApp')}
		</MarketingButton>
	) : (
		<>
			<MarketingButton href={SIGN_IN_HREF} size="sm" variant="secondary">
				{t('login')}
			</MarketingButton>
			<MarketingButton href={SIGN_IN_HREF} size="sm">
				{t('register')}
			</MarketingButton>
		</>
	)

	return (
		<header className="pointer-events-none fixed inset-x-0 top-6 z-[999]">
			<div className="container-marketing flex items-start justify-between gap-4">
				<div className="edge-highlight pointer-events-auto flex items-center gap-1 rounded-lg border border-border/40 bg-glass p-1.5 backdrop-blur-[8px]">
					<Link
						href="/"
						className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-foreground"
					>
						<BrandMark className="h-3.5 w-auto" />
						Shipman
					</Link>
					<span aria-hidden="true" className="mx-1 hidden h-5 w-px bg-border md:block" />
					<nav aria-label="Marketing" className="hidden items-center gap-1 md:flex">
						{MARKETING_NAV.map((item) => (
							<Link
								key={item.key}
								href={item.href}
								className="rounded-lg px-3 py-2.5 text-xs leading-4 font-medium text-body transition-colors duration-150 hover:bg-secondary hover:text-foreground"
							>
								{t(item.key)}
							</Link>
						))}
					</nav>
					<button
						type="button"
						aria-expanded={open}
						aria-controls={MOBILE_MENU_ID}
						aria-label={open ? t('close') : t('menu')}
						onClick={() => setOpen((value) => !value)}
						className="flex size-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-secondary md:hidden"
					>
						{open ? <XIcon className="size-4" /> : <MenuIcon className="size-4" />}
					</button>
				</div>
				<div className="pointer-events-auto hidden items-center gap-2 md:flex">{actions}</div>
			</div>

			<div
				id={MOBILE_MENU_ID}
				hidden={!open}
				className={cn('container-marketing pointer-events-auto mt-2 md:hidden', !open && 'hidden')}
			>
				<div className="edge-highlight flex flex-col gap-1 rounded-lg border border-border/40 bg-glass p-2 backdrop-blur-[8px]">
					{MARKETING_NAV.map((item) => (
						<Link
							key={item.key}
							href={item.href}
							onClick={() => setOpen(false)}
							className="rounded-lg px-3 py-3 text-sm font-medium text-body hover:bg-secondary hover:text-foreground"
						>
							{t(item.key)}
						</Link>
					))}
					<div className="mt-2 flex flex-col gap-2 border-t border-border/60 pt-3 *:justify-center">
						{actions}
					</div>
				</div>
			</div>
		</header>
	)
}
